import { useCallback, useEffect, useState } from "react";
import { ModalBackdrop } from "./ModalBackdrop";
import { useEscapeKey } from "./useEscapeKey";
import { MEMORY_KINDS } from "../memberMemory.js";

/**
 * >>> **WHAT LALA REMEMBERS — A WINDOW SHE CAN READ, AND NOTHING SHE CAN TEAR OUT.** <<<
 *
 * >>> **HER RULING, 2026-09-04, WHICH REPLACES THE ONE THIS FILE WAS BUILT ON:** *members delete the
 * chat instead, Lala keeps whole memory.* <<< The controls are gone: no per-item forget, no
 * clear-all. **The panel is unchanged in every other way** — she can still see everything Lala holds
 * about her, in the words it is held in, which is the half of the original clause that survives and
 * the half that matters most for a feature she has to trust.
 *
 * **THE SUPERSEDED RULING IS KEPT HERE RATHER THAN DELETED**, because a later session reading only
 * the new one cannot tell whether the controls were removed on purpose or lost in a refactor:
 * *"PERSISTENCE UNTIL SHE DELETES IT — a 'forget this' control per memory item AND a clear-all in
 * account settings. BOTH HALVES."* (her clause 2, superseded above.)
 *
 * **THE SERVER ROUTES ARE NOT TOUCHED BY THIS ROUND AND THAT IS DELIBERATE (R17/R160).** The DELETE
 * doors still exist and still answer; what is removed is the way a member reaches them. Ripping out
 * a live route in the same push that removes its only caller is two changes wearing one commit, and
 * the second one is the one that breaks a rail somebody else is holding. It is written down.
 *
 * **THE MEMBER CAN SEE EVERYTHING LALA REMEMBERS ABOUT HER, IN THE WORDS IT IS REMEMBERED IN.** A
 * memory feature whose contents a member cannot read is one she has to take on trust, and this is
 * the most personal thing this house stores about her. Nothing is paraphrased for display: what is
 * on this screen is exactly the text that rides into Lala's context.
 *
 * **A ROW ALREADY MARKED FORGOTTEN STILL READS AS FORGOTTEN.** Members forgot things while the
 * controls existed, and those rows are still on file marked and still excluded from what Lala sees.
 * Hiding that state now would tell a member her memory is whole when part of it is not (R18).
 *
 * >>> **THE READS AND THE WRITES LIVE HERE RATHER THAN IN THE SHELL, AND THAT IS R8's CEILING IN
 * PRACTICE.** <<< The first draft of this feature put eighty lines of fetch and state into
 * `App.tsx`, a file the ratchet only lets get smaller, and the gate refused it on the spot. Out here
 * a fixture can IMPORT this and drive every state; in there it would have been reachable by a regex
 * over ten thousand lines.
 */
export type MemoryItem = {
  id: number;
  kind: string;
  subject: string;
  detail?: string;
  seenCount?: number;
  forgottenAt?: string | null;
};

export type MemoryThread = {
  id: number;
  summary: string;
  forgottenAt?: string | null;
};

const OPEN_FAILED = "That could not be opened just now. Nothing has changed.";

export function LalaMemoryPanel({ onClose }: { onClose: () => void }) {
  useEscapeKey(onClose);
  const [items, setItems] = useState<MemoryItem[]>([]);
  const [threads, setThreads] = useState<MemoryThread[]>([]);
  const [busy, setBusy] = useState(true);
  const [note, setNote] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/lala/memory", { credentials: "include" });
        const data = await res.json().catch(() => null);
        if (!alive) return;
        // **A FAILED READ IS NOT AN EMPTY MEMORY.** Saying "nothing yet" over a read that did not
        // land tells her Lala forgot her, which is a lie this screen has no way to take back
        // (R53/R57/R58's whole family: an empty state is a claim).
        if (!res.ok || !data?.ok) { setNote(OPEN_FAILED); return; }
        setItems(Array.isArray(data.items) ? data.items : []);
        setThreads(Array.isArray(data.threads) ? data.threads : []);
        setLoaded(true);
      } catch {
        if (alive) setNote(OPEN_FAILED);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, []);


  const empty = loaded && items.length === 0 && threads.length === 0;
  return (
    <ModalBackdrop label="What Lala remembers" className="lala-memory-backdrop">
      <div className="lala-memory">
        <button className="modal-close" type="button" onClick={onClose} aria-label="Close what Lala remembers">x</button>
        <p className="eyebrow">Between you and Lala</p>
        <h2>What Lala remembers</h2>
        <p className="lala-memory__lede">
          These are the things you have told her, in her own words, so you never have to say them twice.
          This is everything she holds, and it is yours to read whenever you like.
        </p>

        {empty ? (
          <p className="lala-memory__empty">
            Nothing yet. Once the two of you have really talked, what you told her will show up here.
          </p>
        ) : null}

        {MEMORY_KINDS.map((kind) => {
          const mine = items.filter((i) => i.kind === kind.key);
          if (!mine.length) return null;
          return (
            <section key={kind.key} className="lala-memory__group">
              <h3>{kind.label}</h3>
              <ul className="lala-memory__list">
                {mine.map((item) => (
                  <li key={item.id} className={item.forgottenAt ? "lala-memory__row is-forgotten" : "lala-memory__row"}>
                    <div className="lala-memory__text">
                      <strong>{item.subject}</strong>
                      {item.detail ? <span> — {item.detail}</span> : null}
                    </div>
                    {item.forgottenAt ? <span className="lala-memory__gone">Forgotten</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {threads.length ? (
          <section className="lala-memory__group">
            <h3>The last times you talked</h3>
            <ul className="lala-memory__list">
              {threads.map((t) => (
                <li key={t.id} className={t.forgottenAt ? "lala-memory__row is-forgotten" : "lala-memory__row"}>
                  <div className="lala-memory__text">{t.summary}</div>
                  {t.forgottenAt ? <span className="lala-memory__gone">Forgotten</span> : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {note ? <p className="lala-memory__note">{note}</p> : null}

      </div>
    </ModalBackdrop>
  );
}
